import moment from "moment";
import { useEffect, useState } from "react";
import { Card, Badge, Row, Col, Button, Nav, Modal, Form, Spinner } from "react-bootstrap";
import { CompanyService } from "Services/CompanyService";
import EditSubscription from "./EditSubscription";

interface IProps {
    companyId: string;
}

type TabStatus = "active" | "expired" | "cancelled";

interface IPlanOption {
    _id: string;
    name: string;
    slug: string;
    price: number;
    currency: string;
    billingCycle: string;
}

const TAB_CONFIG: { key: TabStatus; label: string; badgeVariant: string }[] = [
    { key: "active", label: "Active", badgeVariant: "success" },
    { key: "expired", label: "Expired", badgeVariant: "warning" },
    { key: "cancelled", label: "Cancelled", badgeVariant: "danger" },
];

export default function CompanyPlan({ companyId }: IProps) {
    const [activeTab, setActiveTab] = useState<TabStatus>("active");
    const [subscriptions, setSubscriptions] = useState<Record<TabStatus, any>>({
        active: null,
        expired: null,
        cancelled: null,
    });
    const [loading, setLoading] = useState<Record<TabStatus, boolean>>({
        active: false,
        expired: false,
        cancelled: false,
    });

    // Subscribe modal state
    const [showModal, setShowModal] = useState(false);
    const [planOptions, setPlanOptions] = useState<IPlanOption[]>([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState("");
    const [subscribing, setSubscribing] = useState(false);
    const [subscribeError, setSubscribeError] = useState("");

    const [editSubId, setEditSubId] = useState<string | null>(null);

    async function fetchSubscription(status: TabStatus) {
        setLoading((prev) => ({ ...prev, [status]: true }));
        try {
            const res = await CompanyService.getCompanySubscription(companyId, status);
            if (res.status === 200) {
                const data = res.data?.data;
                const subs = Array.isArray(data) ? data : data ? [data] : [];
                setSubscriptions((prev) => ({ ...prev, [status]: subs }));
            }
        } catch (err) {
            console.error(err);
            setSubscriptions((prev) => ({ ...prev, [status]: [] }));
        } finally {
            setLoading((prev) => ({ ...prev, [status]: false }));
        }
    }

    async function openSubscribeModal() {
        setShowModal(true);
        setSelectedPlanId("");
        setSubscribeError("");
        if (planOptions.length === 0) {
            setPlansLoading(true);
            try {
                const res = await CompanyService.getPlansForDropdown();
                if (res.status === 200) {
                    setPlanOptions(res.data?.data || []);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setPlansLoading(false);
            }
        }
    }

    async function handleSubscribe() {
        if (!selectedPlanId) {
            setSubscribeError("Please select a plan.");
            return;
        }
        setSubscribing(true);
        setSubscribeError("");
        try {
            const res = await CompanyService.subscribePlan({
                companyId,
                planId: selectedPlanId,
            });
            if (res.status === 200 || res.status === 201) {
                setShowModal(false);
                // Refresh active tab
                setSubscriptions((prev) => ({ ...prev, active: null }));
                fetchSubscription('active');
            } else {
                setSubscribeError(res.data?.message || "Failed to subscribe. Please try again.");
            }
        } catch (err: any) {
            setSubscribeError(
                err?.response?.data?.message || "Something went wrong. Please try again."
            );
        } finally {
            setSubscribing(false);
        }
    }

    useEffect(() => {
        setSubscriptions({ active: null, expired: null, cancelled: null });
    }, [companyId]);

    useEffect(() => {
        fetchSubscription(activeTab);
    }, [activeTab, companyId]);

    function formatDate(date: string) {
        return moment(date).format("DD MMM YYYY");
    }

    function getExpiryDays(expiryDate: string) {
        return moment(expiryDate).diff(moment(), "days");
    }

    const currentSubs: any[] = subscriptions[activeTab] ?? [];
    const isActive = activeTab === "active";
    const noActiveSub = isActive && !loading["active"] && subscriptions["active"] !== null && currentSubs.length === 0;

    return (
        <>
            <Card className="mb-4 info-card">
                <Card.Body>
                    <h6 className="section-title mb-3">Subscription Plan</h6>

                    <Nav
                        variant="tabs"
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k as TabStatus)}
                        className="mb-4"
                    >
                        {TAB_CONFIG.map((tab) => (
                            <Nav.Item key={tab.key}>
                                <Nav.Link eventKey={tab.key}>
                                    {tab.label}
                                </Nav.Link>
                            </Nav.Item>
                        ))}
                    </Nav>

                    {loading[activeTab] && (
                        <div className="text-center py-4 text-muted">
                            Loading {activeTab} plans…
                        </div>
                    )}

                    {noActiveSub && (
                        <div className="text-center py-5">
                            <div className="mb-3" style={{ fontSize: "3rem" }}>📋</div>
                            <h6 className="text-muted mb-1">No Active Subscription</h6>
                            <p className="text-muted small mb-3">
                                This company doesn't have an active plan. Subscribe to get started.
                            </p>
                            <Button className="save-plan-btn" onClick={openSubscribeModal}>
                                Subscribe a Plan
                            </Button>
                        </div>
                    )}

                    {!loading[activeTab] && !noActiveSub && currentSubs.length === 0 && subscriptions[activeTab] !== null && (
                        <div className="text-center py-4 text-muted">
                            No {activeTab} plans found.
                        </div>
                    )}

                    {!loading[activeTab] &&
                        currentSubs.map((subscription: any, idx: number) => (
                            <div key={idx} className="assigned-plan-card p-4 mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <h5 className="fw-bold mb-1">
                                            {subscription?.plan?.name}
                                        </h5>
                                        <small className="text-muted">
                                            {subscription?.plan?.currency}{" "}
                                            {subscription?.plan?.price} /{" "}
                                            {subscription?.plan?.billingCycle}
                                        </small>
                                    </div>
                                    <Badge
                                        bg={
                                            TAB_CONFIG.find((t) => t.key === activeTab)
                                                ?.badgeVariant
                                        }
                                        className="plan-chip"
                                    >
                                        {subscription?.status}
                                    </Badge>
                                </div>

                                <Row>
                                    <Col md={4}>
                                        <p className="label">Start Date</p>
                                        <p className="value">
                                            {subscription?.startDate
                                                ? formatDate(subscription.startDate)
                                                : "-"}
                                        </p>
                                    </Col>
                                    <Col md={4}>
                                        <p className="label">Expiry Date</p>
                                        <p className="value">
                                            {subscription?.expiryDate
                                                ? formatDate(subscription.expiryDate)
                                                : "-"}
                                        </p>
                                    </Col>
                                </Row>

                                <div className="mt-3 text-end">
                                    <Button
                                        size="sm"
                                        className="save-plan-btn me-2"
                                        onClick={() => setEditSubId(subscription?._id)}
                                    >
                                        Change Plan
                                    </Button>
                                </div>

                                <h5 className="fw-bold mb-1 mt-4">Plan Usage Limits</h5>
                                <Row className="mt-4 mb-4">
                                    {subscription?.plan?.limits?.map((key: any) => (
                                        <Col md={4} key={key?.key}>
                                            <div className="usage-card">
                                                <h6 className="text-capitalize">{key?.key}</h6>
                                                <h4>{key?.value}</h4>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>

                                {isActive && subscription?.expiryDate && (
                                    <div
                                        className={`expiry-box p-3 ${getExpiryDays(subscription.expiryDate) <= 7
                                                ? "expiry-warning"
                                                : ""
                                            }`}
                                    >
                                        <Row className="align-items-center">
                                            <Col md={8}>
                                                <strong>Plan Expiry:</strong>{" "}
                                                {formatDate(subscription.expiryDate)}
                                                <div>
                                                    {getExpiryDays(subscription.expiryDate) > 0 ? (
                                                        <span className="text-muted">
                                                            {getExpiryDays(subscription.expiryDate)}{" "}
                                                            days remaining
                                                        </span>
                                                    ) : (
                                                        <span className="text-danger">
                                                            Plan Expired
                                                        </span>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                )}

                                <h5 className="fw-bold mb-1 mt-4">Plan AI Features</h5>
                                <Row className="mt-4 mb-4">
                                    {subscription?.plan?.features?.map((key: any) => (
                                        <Col md={4} key={key}>
                                            <div className="usage-card">
                                                <h6 className="text-capitalize">{key}</h6>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        ))}
                </Card.Body>
            </Card>

            {editSubId && (
                <EditSubscription
                    show={!!editSubId}
                    subId={editSubId}
                    companyId={companyId}
                    onHide={() => setEditSubId(null)}
                    onSuccess={() => {
                        setEditSubId(null);
                        setSubscriptions((prev) => ({ ...prev, active: null }));
                        fetchSubscription("active");
                    }}
                />
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Subscribe a Plan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {plansLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Loading plans…
                        </div>
                    ) : (
                        <Form>
                            <Form.Group controlId="planSelect">
                                <Form.Label className="fw-semibold mb-2">Select Plan</Form.Label>
                                <Form.Select
                                    value={selectedPlanId}
                                    onChange={(e) => {
                                        setSelectedPlanId(e.target.value);
                                        setSubscribeError("");
                                    }}
                                    isInvalid={!!subscribeError && !selectedPlanId}
                                >
                                    <option value="">— Choose a plan —</option>
                                    {planOptions.map((plan) => (
                                        <option key={plan._id} value={plan._id}>
                                            {plan.name} — {plan.currency} {plan.price} / {plan.billingCycle}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            {subscribeError && (
                                <div className="text-danger small mt-2">{subscribeError}</div>
                            )}
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowModal(false)}
                        disabled={subscribing}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="save-plan-btn"
                        onClick={handleSubscribe}
                        disabled={subscribing || plansLoading}
                    >
                        {subscribing ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Subscribing…
                            </>
                        ) : (
                            "Subscribe"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}